import React, { useCallback, useRef, useState } from 'react'

import { type LucideIcon } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'

export interface CreateEntityDialogSection {
  id: string
  label: string
  description?: string
  icon: LucideIcon
  required?: boolean
  headerAccessory?: React.ReactNode
  children: React.ReactNode
}

interface CreateEntityDialogLayoutProps {
  navLabel: string
  title: string
  description?: string
  sections: CreateEntityDialogSection[]
  footer: React.ReactNode
}

interface CreateEntityFieldShellProps {
  label: string
  required?: boolean
  fullWidth?: boolean
  children: React.ReactNode
}

const SectionNavButton: React.FC<{
  section: CreateEntityDialogSection
  active: boolean
  onClick: () => void
}> = ({ section, active, onClick }) => {
  const Icon = section.icon

  return (
    <button
      type="button"
      aria-current={active ? 'true' : undefined}
      onClick={onClick}
      className={twMerge(
        'group relative flex h-9 w-full items-center gap-2 rounded-md px-3 text-left text-sm font-medium transition-colors',
        'text-primary-700 hover:bg-primary-100 hover:text-primary-950 dark:text-primary-300 dark:hover:bg-primary-800 dark:hover:text-primary-50',
        active &&
          'bg-primary-100 text-blue-700 hover:text-blue-700 dark:bg-primary-800/80 dark:text-blue-300 dark:hover:text-blue-300',
      )}
    >
      {active ? (
        <span className="absolute left-0 top-1.5 h-6 w-0.5 rounded-full bg-blue-600 dark:bg-blue-400" />
      ) : null}
      <span
        className={twMerge(
          'flex size-5 shrink-0 items-center justify-center text-primary-500 transition-colors dark:text-primary-400',
          active
            ? 'text-blue-700 dark:text-blue-300'
            : 'group-hover:text-primary-700 dark:group-hover:text-primary-200',
        )}
      >
        <Icon className="size-3.5" aria-hidden={true} />
      </span>
      <span className="min-w-0 flex-1 truncate">{section.label}</span>
    </button>
  )
}

export const CreateEntityFieldShell: React.FC<CreateEntityFieldShellProps> = ({
  label,
  required = false,
  fullWidth = false,
  children,
}) => {
  return (
    <div
      className={twMerge(
        'flex min-w-0 flex-col gap-1',
        fullWidth && 'md:col-span-2',
      )}
    >
      <label className="text-[13px] font-medium text-primary-700 dark:text-primary-300">
        {label}
        {required ? <span className="text-accent-500">*</span> : null}
      </label>
      {children}
    </div>
  )
}

export const CreateEntityDialogLayout: React.FC<
  CreateEntityDialogLayoutProps
> = ({ navLabel, title, description, sections, footer }) => {
  const initialSectionId = sections[0]?.id ?? ''
  const [activeSectionId, setActiveSectionId] = useState(initialSectionId)
  const activeSectionIdRef = useRef(initialSectionId)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({})
  const ignoreScrollSpyUntilRef = useRef(0)

  const setActiveSection = useCallback((sectionId: string) => {
    activeSectionIdRef.current = sectionId
    setActiveSectionId(sectionId)
  }, [])

  const handleNavigate = useCallback(
    (sectionId: string) => {
      const contentElement = contentRef.current
      const sectionElement = sectionRefs.current[sectionId]
      setActiveSection(sectionId)
      ignoreScrollSpyUntilRef.current = Date.now() + 500

      if (!contentElement || !sectionElement) {
        return
      }

      const contentRect = contentElement.getBoundingClientRect()
      const sectionRect = sectionElement.getBoundingClientRect()

      contentElement.scrollTo({
        top: contentElement.scrollTop + sectionRect.top - contentRect.top - 16,
        behavior: 'smooth',
      })
    },
    [setActiveSection],
  )

  const handleContentScroll = useCallback(() => {
    const contentElement = contentRef.current

    if (!contentElement || Date.now() < ignoreScrollSpyUntilRef.current) {
      return
    }

    const contentTop = contentElement.getBoundingClientRect().top
    let closestSectionId = activeSectionIdRef.current
    let closestDistance = Number.POSITIVE_INFINITY

    for (const section of sections) {
      const sectionElement = sectionRefs.current[section.id]

      if (!sectionElement) {
        continue
      }

      const distance = Math.abs(
        sectionElement.getBoundingClientRect().top - contentTop - 20,
      )

      if (distance < closestDistance) {
        closestDistance = distance
        closestSectionId = section.id
      }
    }

    if (closestSectionId !== activeSectionIdRef.current) {
      setActiveSection(closestSectionId)
    }
  }, [sections, setActiveSection])

  return (
    <div className="flex min-h-0 flex-1">
      <aside className="hidden w-60 shrink-0 border-r border-primary-200 bg-primary-50/70 p-4 dark:border-primary-800 dark:bg-primary-950/40 md:block">
        <div className="mb-3 px-2 text-sm font-semibold text-primary-800 dark:text-primary-200">
          {navLabel}
        </div>
        <nav className="space-y-1">
          {sections.map((section) => (
            <SectionNavButton
              key={section.id}
              section={section}
              active={section.id === activeSectionId}
              onClick={() => handleNavigate(section.id)}
            />
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <DialogHeader className="border-b border-primary-200 px-5 py-4 dark:border-primary-800">
          <DialogTitle className="truncate mr-8">{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>

        <div className="border-b border-primary-200 px-4 py-2 dark:border-primary-800 md:hidden">
          <div className="flex gap-1 overflow-x-auto">
            {sections.map((section) => (
              <button
                key={section.id}
                type="button"
                aria-current={
                  section.id === activeSectionId ? 'true' : undefined
                }
                onClick={() => handleNavigate(section.id)}
                className={twMerge(
                  'h-7 shrink-0 rounded-[5px] px-2 text-sm font-medium text-primary-700 dark:text-primary-300',
                  section.id === activeSectionId &&
                    'bg-primary-200 text-primary-950 dark:bg-primary-800 dark:text-primary-50',
                )}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>

        <div
          ref={contentRef}
          onScroll={handleContentScroll}
          className="flex-1 overflow-y-auto"
        >
          <div>
            {sections.map((section) => (
              <section
                key={section.id}
                ref={(element) => {
                  sectionRefs.current[section.id] = element
                }}
                className="scroll-mt-5 border-t border-primary-200 first:border-t-0 dark:border-primary-800"
              >
                <div className="flex min-w-0 flex-col gap-3 px-5 pt-5 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-sm font-semibold text-primary-950 dark:text-primary-50">
                      {section.label}
                    </h3>
                    {section.description ? (
                      <p className="mt-1 text-xs leading-5 text-primary-500 dark:text-primary-400">
                        {section.description}
                      </p>
                    ) : null}
                  </div>
                  {section.headerAccessory ? (
                    <div className="shrink-0">{section.headerAccessory}</div>
                  ) : null}
                </div>
                <div className="px-5 pb-6 pt-4">{section.children}</div>
              </section>
            ))}
          </div>
        </div>

        {footer}
      </div>
    </div>
  )
}
